import { useLayoutEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check, Close, ErrorOutline, ExpandLess, ExpandMore, HelpOutline } from '@mui/icons-material';

const SELETOR_HEADER = 'header';
const TOPO_PADRAO = 72;
const ESPACO = 12;
const CHAVE_MINIMIZADO = 'processamentos:minimizado';

const VERBOS = {
    criar: 'Criando',
    atualizar: 'Atualizando',
    status: 'Alterando status de',
};

const nomeDoGrupo = (item) => item.nome_grupo ?? item.nomeGrupo ?? null;
const idDoGrupo = (item) => item.id_grupo ?? item.idGrupo ?? null;

function lerMinimizado() {
    try {
        return window.localStorage.getItem(CHAVE_MINIMIZADO) === '1';
    } catch {
        return false;
    }
}

function salvarMinimizado(valor) {
    try {
        window.localStorage.setItem(CHAVE_MINIMIZADO, valor ? '1' : '0');
    } catch {
        return;
    }
}

function useAlturaHeader() {
    const [topo, setTopo] = useState(TOPO_PADRAO);

    useLayoutEffect(() => {
        const medir = () => {
            const header = document.querySelector(SELETOR_HEADER);
            const base = header ? header.getBoundingClientRect().bottom : TOPO_PADRAO;
            setTopo(Math.max(0, Math.round(base)) + ESPACO);
        };

        medir();

        const header = document.querySelector(SELETOR_HEADER);
        const observer = header && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;

        observer?.observe(header);
        window.addEventListener('resize', medir);

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', medir);
        };
    }, []);

    return topo;
}

function textos(item) {
    const nome = nomeDoGrupo(item) ? `"${nomeDoGrupo(item)}"` : 'grupo';

    if (item.status === 'processando') {
        return {
            titulo: `${VERBOS[item.acao] ?? 'Atualizando'} ${nome}…`,
            descricao: 'As permissões e o estoque estão sendo processados. Você pode continuar usando o sistema — avisamos quando terminar.',
        };
    }

    if (item.status === 'erro') {
        return {
            titulo: 'Não foi possível concluir',
            descricao: `O grupo ${nome} foi salvo, mas o processamento das permissões falhou. Tente novamente.`,
        };
    }

    if (item.status === 'expirado') {
        return {
            titulo: 'Acompanhamento encerrado',
            descricao: `Não foi possível acompanhar o processamento de ${nome}. Confira o grupo antes de editar novamente.`,
        };
    }

    return {
        titulo: item.acao === 'criar' ? 'Grupo criado' : 'Grupo atualizado',
        descricao: `Permissões e estoque de ${nome} foram processados com sucesso.`,
    };
}

function resumo(visiveis) {
    const emAndamento = visiveis.filter((item) => item.status === 'processando').length;
    const comErro = visiveis.filter((item) => item.status === 'erro').length;
    const concluidos = visiveis.length - emAndamento - comErro;

    const titulo = emAndamento
        ? `Processando ${emAndamento === 1 ? '1 grupo' : `${emAndamento} grupos`}`
        : comErro
            ? 'Processamento com erro'
            : 'Processamento concluído';

    const partes = [
        emAndamento && `${emAndamento} em andamento`,
        concluidos && `${concluidos} ${concluidos === 1 ? 'finalizado' : 'finalizados'}`,
        comErro && `${comErro} com erro`,
    ].filter(Boolean);

    return {
        titulo,
        subtitulo: partes.join(' · '),
        status: emAndamento ? 'processando' : comErro ? 'erro' : 'concluido',
        emAndamento,
    };
}

function IconeStatus({ status, tamanho = 32 }) {
    return (
        <Box
            sx={(theme) => {
                const cheio = status === 'concluido';
                const cor = status === 'erro'
                    ? theme.palette.error.main
                    : status === 'expirado'
                        ? theme.palette.text.secondary
                        : theme.palette.primary.main;

                return {
                    width: tamanho,
                    height: tamanho,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: cheio ? cor : alpha(cor, 0.12),
                    color: cheio ? theme.palette.primary.contrastText : cor,
                };
            }}
        >
            {status === 'processando' && <CircularProgress size={tamanho / 2} thickness={5} color="inherit" />}
            {status === 'erro' && <ErrorOutline fontSize="small" />}
            {status === 'expirado' && <HelpOutline fontSize="small" />}
            {status === 'concluido' && <Check fontSize="small" />}
        </Box>
    );
}

function ItemProcessamento({ item, onClose, onOpen }) {
    const { titulo, descricao } = textos(item);
    const emAndamento = item.status === 'processando';
    const comErro = item.status === 'erro';

    return (
        <Box sx={{ px: 2, py: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <IconeStatus status={item.status} />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} noWrap title={titulo}>
                        {titulo}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {descricao}
                    </Typography>

                    {!emAndamento && idDoGrupo(item) && (comErro ? (
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => onOpen(item)}
                            sx={{ mt: 1 }}
                        >
                            Tentar novamente
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            onClick={() => onOpen(item)}
                            sx={{ mt: 0.5, px: 0, minWidth: 0, fontWeight: 700 }}
                        >
                            Ver grupo
                        </Button>
                    ))}
                </Box>

                <Tooltip title={emAndamento ? 'Ocultar até terminar' : 'Fechar'}>
                    <IconButton size="small" onClick={() => onClose(item)}>
                        <Close fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {emAndamento && (
                <LinearProgress
                    variant={item.progresso == null ? 'indeterminate' : 'determinate'}
                    value={item.progresso ?? 0}
                    sx={{ mt: 1.5, borderRadius: 1 }}
                />
            )}
        </Box>
    );
}

export function FilaProcessamentos({ itens, onClose, onOpen }) {
    const topo = useAlturaHeader();
    const [minimizado, setMinimizado] = useState(lerMinimizado);

    const visiveis = itens.filter((item) => !item.oculto);

    if (!visiveis.length) return null;

    const { titulo, subtitulo, status, emAndamento } = resumo(visiveis);
    const finalizados = visiveis.filter((item) => item.status !== 'processando');

    const alternar = () => {
        setMinimizado((valor) => {
            salvarMinimizado(!valor);
            return !valor;
        });
    };

    return (
        <Box
            sx={(theme) => ({
                position: 'fixed',
                top: topo,
                right: 16,
                width: 'min(380px, calc(100vw - 32px))',
                zIndex: theme.zIndex.modal - 1,
            })}
        >
            <Paper
                elevation={8}
                sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider' }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    onClick={alternar}
                    sx={{
                        px: 2,
                        py: 1.25,
                        cursor: 'pointer',
                        userSelect: 'none',
                        borderBottom: minimizado ? 0 : 1,
                        borderColor: 'divider',
                    }}
                >
                    <IconeStatus status={status} tamanho={28} />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {titulo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {subtitulo}
                        </Typography>
                    </Box>

                    {!minimizado && finalizados.length > 1 && (
                        <Button
                            size="small"
                            onClick={(evento) => {
                                evento.stopPropagation();
                                finalizados.forEach((item) => onClose(item));
                            }}
                        >
                            Limpar
                        </Button>
                    )}

                    <Tooltip title={minimizado ? 'Expandir' : 'Recolher'}>
                        <IconButton
                            size="small"
                            onClick={(evento) => {
                                evento.stopPropagation();
                                alternar();
                            }}
                        >
                            {minimizado ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                    </Tooltip>
                </Stack>

                {minimizado && emAndamento > 0 && <LinearProgress sx={{ height: 3 }} />}

                <Collapse in={!minimizado} timeout="auto" unmountOnExit>
                    <Box sx={{ maxHeight: `calc(100vh - ${topo + 120}px)`, overflowY: 'auto' }}>
                        {visiveis.map((item, indice) => (
                            <Box key={item.id} sx={{ borderTop: indice ? 1 : 0, borderColor: 'divider' }}>
                                <ItemProcessamento item={item} onClose={onClose} onOpen={onOpen} />
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            </Paper>
        </Box>
    );
}